import { useQuery } from '@tanstack/react-query';
import { Users, TrendingUp, TrendingDown } from 'lucide-react';
import type { Institutional } from '@invest-copilot/shared';
import { fetchJSON as getJSON } from '../lib/api-client';
import { isDelistedError, DELISTED_MESSAGE } from '../lib/error-utils';

/**
 * 三大法人買賣超卡片（僅台股顯示）
 * 外資連賣 5 天通常意味偏空；投信買超代表中線題材；自營商隔日沖多
 */
export function InstitutionalCard({ symbol }: { symbol: string }) {
  const isTW = /\.(TW|TWO)$/i.test(symbol);

  const q = useQuery({
    queryKey: ['institutional', symbol],
    queryFn: () => getJSON<Institutional>(`/api/institutional/${encodeURIComponent(symbol)}`),
    enabled: isTW && !!symbol,
    staleTime: 60 * 60_000, // 1 小時（資料一天才更新）
    refetchOnMount: false,
    retry: 2,
    retryDelay: (i) => 500 * (i + 1),
  });

  if (!isTW) return null;
  if (q.isLoading) {
    return (
      <div className="card">
        <div className="text-(--text-muted) text-sm py-4 text-center">載入籌碼資料…</div>
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Users size={16} className="text-(--accent)" />
          <div className="font-bold text-(--text-primary) text-sm">三大法人</div>
        </div>
        <div className={isDelistedError(q.error) ? 'text-(--text-muted) text-xs' : 'text-(--up) text-xs'}>{isDelistedError(q.error) ? DELISTED_MESSAGE : `無法取得：${(q.error as Error).message}`}</div>
      </div>
    );
  }
  if (!q.data || q.data.days.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Users size={16} className="text-(--accent)" />
          <div className="font-bold text-(--text-primary) text-sm">三大法人</div>
        </div>
        <div className="text-(--text-muted) text-xs">尚無籌碼資料（可能是新上市或下市）</div>
      </div>
    );
  }

  const f = q.data;
  // 計算最近一日方向 → 標籤
  const today = f.days[0]!;
  const todayLabel = labelFromTotal(today.total);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-(--accent)" />
          <div>
            <div className="font-bold text-(--text-primary) text-sm">三大法人籌碼</div>
            <div className="text-[10px] text-(--text-muted)">近 5 日買賣超（單位：張）· TWSE 收盤後更新</div>
          </div>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${todayLabel.cls}`}>
          {todayLabel.text}
        </span>
      </div>

      {/* 5 日累計 stats */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <SumStat label="外資" value={f.totalForeign5d} />
        <SumStat label="投信" value={f.totalTrust5d} />
        <SumStat label="自營" value={f.totalDealer5d} />
        <SumStat label="合計" value={f.totalAll5d} bold />
      </div>

      {/* 每日表格 */}
      <table className="w-full text-[11px] tnum">
        <thead>
          <tr className="text-(--text-muted) text-left border-b border-(--border)">
            <th className="py-0.5 pr-2">日期</th>
            <th className="py-0.5 pr-2 text-right">外資</th>
            <th className="py-0.5 pr-2 text-right">投信</th>
            <th className="py-0.5 pr-2 text-right">自營</th>
            <th className="py-0.5 pr-2 text-right">合計</th>
          </tr>
        </thead>
        <tbody>
          {f.days.map((d) => (
            <tr key={d.date} className="border-b border-(--border)/50">
              <td className="py-0.5 pr-2 text-(--text-secondary)">{d.date.slice(5)}</td>
              <DayCell value={d.foreign} />
              <DayCell value={d.trust} />
              <DayCell value={d.dealer} />
              <DayCell value={d.total} bold />
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-2 text-[10px] text-(--text-muted) leading-relaxed">
        💡 外資連續買超通常意味中長線看好；投信買超多為短中線題材；自營商常隔日沖。
      </div>
    </div>
  );
}

function SumStat({ label, value, bold = false }: { label: string; value: number; bold?: boolean }) {
  const cls = value > 0 ? 'text-(--up)' : value < 0 ? 'text-(--down)' : 'text-(--text-primary)';
  return (
    <div className="p-1.5 rounded bg-(--bg-base) border border-(--border) text-center">
      <div className="text-[9px] text-(--text-muted)">{label}</div>
      <div className={`text-xs tnum ${bold ? 'font-bold' : 'font-semibold'} ${cls}`}>
        {value >= 0 ? '+' : ''}{value.toLocaleString()}
      </div>
    </div>
  );
}

function DayCell({ value, bold = false }: { value: number; bold?: boolean }) {
  const cls = value > 0 ? 'text-(--up)' : value < 0 ? 'text-(--down)' : 'text-(--text-muted)';
  return (
    <td className={`py-0.5 pr-2 text-right ${cls} ${bold ? 'font-bold' : ''}`}>
      {value >= 0 ? '+' : ''}{value.toLocaleString()}
    </td>
  );
}

function labelFromTotal(total: number): { text: string; cls: string } {
  if (total > 1000) return { text: '🔥 三大法人大買', cls: 'border-(--up) bg-(--up)/15 text-(--up)' };
  if (total > 100) return { text: '↑ 法人偏多', cls: 'border-(--up)/60 bg-(--up)/10 text-(--up)' };
  if (total < -1000) return { text: '🥶 三大法人大賣', cls: 'border-(--down) bg-(--down)/15 text-(--down)' };
  if (total < -100) return { text: '↓ 法人偏空', cls: 'border-(--down)/60 bg-(--down)/10 text-(--down)' };
  return { text: '— 法人觀望', cls: 'border-(--border) text-(--text-muted)' };
}
